/**
 * 对话框状态管理 Store
 *
 * 统一管理全局对话框的开关状态。
 * 从 uiStore.ts 拆分而来（P2-1 巨型 Store 拆解）。
 *
 * 薄DDD重构 - 支持事件总线，解决循环依赖
 *
 * @module shared/stores/dialogStore
 */

import { defineStore } from "pinia";
import { reactive, ref, computed, type ComputedRef, type Ref } from "vue";
import { eventBus } from "@shared/domain/events/EventBus";
import { DialogEvents } from "@shared/domain/dialog/DialogEvents";

// ==================== 类型定义 ====================

/** 对话框类型 */
export type DialogType = "help" | "contact" | "profile";

// ==================== 对话框常量定义 ====================

/** 患者相关对话框 */
export const PATIENT_DIALOGS = {
  patientEdit: "patientEdit" as const,
  patientDelete: "patientDelete" as const,
  patientDetail: "patientDetail" as const,
} as const;

/** 医疗记录相关对话框 */
export const RECORD_DIALOGS = {
  recordEdit: "recordEdit" as const,
  recordDelete: "recordDelete" as const,
  recordDetail: "recordDetail" as const,
} as const;

/** 数据提取相关对话框 */
export const EXTRACT_DIALOGS = {
  dataExtract: "dataExtract" as const,
  extractResult: "extractResult" as const,
  selectiveData: "selectiveData" as const,
} as const;

/** 健康计划相关对话框 */
export const HEALTH_PLAN_DIALOGS = {
  healthPlan: "healthPlan" as const,
} as const;

/** 标签管理相关对话框 */
export const TAG_DIALOGS = {
  tagManage: "tagManage" as const,
  tagEdit: "tagEdit" as const,
  tagForm: "tagForm" as const,
  tagAnnotate: "tagAnnotate" as const,
} as const;

/** 系统设置相关对话框 */
export const SYSTEM_DIALOGS = {
  settings: "settings" as const,
  about: "about" as const,
} as const;

/** 登录认证相关对话框 */
export const AUTH_DIALOGS = {
  login: "login" as const,
  register: "register" as const,
  forgotPassword: "forgotPassword" as const,
  profileEditor: "profileEditor" as const,
  helpModal: "helpModal" as const,
  contactModal: "contactModal" as const,
} as const;

/** 确认对话框 */
export const CONFIRM_DIALOGS = {
  confirm: "confirm" as const,
} as const;

/** 所有预定义对话框常量 */
export const ALL_DIALOGS = {
  ...PATIENT_DIALOGS,
  ...RECORD_DIALOGS,
  ...EXTRACT_DIALOGS,
  ...HEALTH_PLAN_DIALOGS,
  ...TAG_DIALOGS,
  ...SYSTEM_DIALOGS,
  ...AUTH_DIALOGS,
  ...CONFIRM_DIALOGS,
} as const;

/** 对话框名称类型 */
export type DialogName =
  | (typeof ALL_DIALOGS)[keyof typeof ALL_DIALOGS]
  | string;

/** 对话框状态类型 */
export interface DialogState {
  // 患者相关对话框
  patientEdit: boolean;
  patientDelete: boolean;
  patientDetail: boolean;

  // 医疗记录相关对话框
  recordEdit: boolean;
  recordDelete: boolean;
  recordDetail: boolean;

  // 数据提取相关对话框
  dataExtract: boolean;
  extractResult: boolean;
  selectiveData: boolean;

  // 健康计划相关对话框
  healthPlan: boolean;

  // 标签管理相关对话框
  tagManage: boolean;
  tagEdit: boolean;
  tagForm: boolean;
  tagAnnotate: boolean;

  // 系统设置相关对话框
  settings: boolean;
  about: boolean;

  // 登录认证相关对话框
  login: boolean;
  register: boolean;
  forgotPassword: boolean;
  profileEditor: boolean;
  helpModal: boolean;
  contactModal: boolean;

  // 确认对话框
  confirm: boolean;

  // 自定义对话框
  custom: Record<string, boolean>;
}

// ==================== Store 定义 ====================

export const useDialogStore = defineStore("dialog", () => {
  // ==================== 事件监听 (薄DDD重构) ====================

  let unsubscribeTagManage: (() => void) | null = null;
  let unsubscribeTagForm: (() => void) | null = null;
  let unsubscribeTagAnnotate: (() => void) | null = null;

  /**
   * 设置事件监听器
   * 用于解耦domain层和UI层
   */
  const setupEventListeners = () => {
    // 监听标签管理对话框事件
    unsubscribeTagManage = eventBus.on(DialogEvents.TAG_MANAGE_OPEN, () => {
      openDialog("tagManage");
    });
    eventBus.on(DialogEvents.TAG_MANAGE_CLOSE, () => {
      closeDialog("tagManage");
    });

    // 监听标签表单对话框事件
    unsubscribeTagForm = eventBus.on(DialogEvents.TAG_FORM_OPEN, () => {
      openDialog("tagForm");
    });
    eventBus.on(DialogEvents.TAG_FORM_CLOSE, () => {
      closeDialog("tagForm");
    });

    // 监听标签标注对话框事件
    unsubscribeTagAnnotate = eventBus.on(DialogEvents.TAG_ANNOTATE_OPEN, () => {
      openDialog("tagAnnotate");
    });
    eventBus.on(DialogEvents.TAG_ANNOTATE_CLOSE, () => {
      closeDialog("tagAnnotate");
    });
  };

  /**
   * 清理事件监听器
   */
  const cleanupEventListeners = () => {
    unsubscribeTagManage?.();
    unsubscribeTagForm?.();
    unsubscribeTagAnnotate?.();
  };

  // ==================== 对话框状态 ====================

  const dialogs = reactive<DialogState>({
    // 患者相关对话框
    patientEdit: false,
    patientDelete: false,
    patientDetail: false,

    // 医疗记录相关对话框
    recordEdit: false,
    recordDelete: false,
    recordDetail: false,

    // 数据提取相关对话框
    dataExtract: false,
    extractResult: false,
    selectiveData: false,

    // 健康计划相关对话框
    healthPlan: false,

    // 标签管理相关对话框
    tagManage: false,
    tagEdit: false,
    tagForm: false,
    tagAnnotate: false,

    // 系统设置相关对话框
    settings: false,
    about: false,

    // 登录认证相关对话框
    login: false,
    register: false,
    forgotPassword: false,
    profileEditor: false,
    helpModal: false,
    contactModal: false,

    // 确认对话框
    confirm: false,

    // 自定义对话框
    custom: {},
  });

  // ==================== 计算属性 ====================

  /** 是否有打开的对话框 */
  const hasOpenDialog: ComputedRef<boolean> = computed(() => {
    return Object.values(dialogs).some((value) => {
      if (typeof value === "boolean") return value;
      if (typeof value === "object") return Object.values(value).some((v) => v);
      return false;
    });
  });

  // ==================== 对话框数据传递 ====================

  /** 对话框间传递的临时数据 */
  const dialogData: Ref<Record<string, unknown>> = ref({});

  /** 设置对话框传递数据 */
  const setDialogData = (key: string, value: unknown): void => {
    dialogData.value[key] = value;
  };

  /** 获取对话框传递数据 */
  const getDialogData = (key: string): unknown => {
    return dialogData.value[key];
  };

  // ==================== 对话框操作方法 ====================

  /** 打开对话框（支持任意字符串名，未知名自动存入 custom 记录） */
  const openDialog = (
    dialogName: DialogName,
    customKey: string | null = null,
  ): void => {
    if (customKey && dialogName === "custom") {
      dialogs.custom[customKey] = true;
    } else if (dialogName in dialogs) {
      (dialogs as any)[dialogName] = true;
    } else {
      // 未知对话框名同时写入顶层响应式状态和 custom 记录。
      // DialogManager 通过 dialogs[config.name] 渲染配置型弹窗，必须能读到顶层 key。
      (dialogs as any)[dialogName] = true;
      dialogs.custom[dialogName] = true;
    }
  };

  /** 关闭对话框（支持任意字符串名，未知名自动从 custom 记录关闭） */
  const closeDialog = (
    dialogName: DialogName,
    customKey: string | null = null,
  ): void => {
    if (customKey && dialogName === "custom") {
      dialogs.custom[customKey] = false;
    } else if (dialogName in dialogs) {
      (dialogs as any)[dialogName] = false;
      if (dialogName in dialogs.custom) {
        dialogs.custom[dialogName] = false;
      }
    } else {
      (dialogs as any)[dialogName] = false;
      dialogs.custom[dialogName] = false;
    }
  };

  /** 关闭所有对话框 */
  const closeAllDialogs = (): void => {
    Object.keys(dialogs).forEach((key) => {
      if (key === "custom") {
        Object.keys(dialogs.custom).forEach((customKey) => {
          dialogs.custom[customKey] = false;
        });
      } else {
        (dialogs as any)[key] = false;
      }
    });
  };

  /** 检查对话框是否打开（支持任意字符串名） */
  const isDialogOpen = (
    dialogName: DialogName,
    customKey: string | null = null,
  ): boolean => {
    if (customKey && dialogName === "custom") {
      return dialogs.custom[customKey] || false;
    } else if (dialogName in dialogs) {
      return (dialogs as any)[dialogName];
    }
    return Boolean((dialogs as any)[dialogName] || dialogs.custom[dialogName]);
  };

  return {
    dialogs,
    dialogData,
    hasOpenDialog,
    openDialog,
    closeDialog,
    closeAllDialogs,
    isDialogOpen,
    setDialogData,
    getDialogData,
    setupEventListeners,
    cleanupEventListeners,
  };
});
